import type { ArrowHead, Point2D } from './contour-draw-geometry';

import * as d3 from 'd3';

import {
  ARROW_HEAD_LENGTH,
  ARROW_HEAD_WIDTH,
  CENTROID_RADIUS,
  CONTOUR_STROKE,
  LABEL_FONT,
} from './contour-draw-constants';
import { arrowHeadGeometry, getLineGenerator } from './contour-draw-geometry';

// d3 selections are loosely typed here; these helpers are shared by the
// contour SVG overlays and all draw in image-pixel space inside a group that
// carries the image->screen matrix (see applyGroupTransform).
type Selection = d3.Selection<d3.BaseType, unknown, d3.BaseType, unknown>;

export function appendPath(
  group: Selection,
  points: Point2D[],
  color: string,
  opacity: number,
  strokeWidth: number,
) {
  return group
    .append('path')
    .datum(points)
    .attr('fill', 'none')
    .attr('opacity', opacity)
    .attr('stroke', color)
    .attr('stroke-width', strokeWidth)
    .attr('vector-effect', 'non-scaling-stroke')
    .attr('d', getLineGenerator());
}

// Shared arrow shape (shaft + filled triangle head) used by both the committed
// renderer (UIDDisplayContours) and the live preview (UIDAdjustContours). All
// coordinates are in the group's image-pixel space. Returns the arrowhead
// geometry (or null for a degenerate arrow) so callers can add extra decoration
// (e.g. a selection highlight along the shaft).
export function appendArrow(
  group: Selection,
  start: Point2D,
  end: Point2D,
  color: string,
  opacity = 1,
): ArrowHead | null {
  const head = arrowHeadGeometry(start, end, ARROW_HEAD_LENGTH, ARROW_HEAD_WIDTH);
  const shaftEnd = head ? head.shaftEnd : end;

  // shaft stops at shaftEnd so the head isn't doubled
  appendPath(group, [start, shaftEnd], color, opacity, CONTOUR_STROKE);

  if (head) {
    group
      .append('path')
      .datum([end, head.leftBase, head.rightBase, end])
      .attr('fill', color)
      .attr('stroke', 'none')
      .attr('opacity', opacity)
      .attr('d', getLineGenerator());
  }

  return head;
}

export interface CircleOpts {
  fill?: string;
  fillOpacity?: number;
  opacity?: number;
  stroke?: string;
  strokeWidth?: number;
}

export function appendCircle(
  group: Selection,
  cx: number,
  cy: number,
  radius: number,
  opts: CircleOpts = {},
) {
  const circle = group
    .append('circle')
    .attr('cx', cx)
    .attr('cy', cy)
    .attr('original-r', radius)
    .attr('r', radius)
    .attr('fill', opts.fill ?? 'none');
  if (opts.fillOpacity != null) circle.attr('fill-opacity', opts.fillOpacity);
  if (opts.opacity != null) circle.attr('opacity', opts.opacity);
  if (opts.stroke) {
    circle
      .attr('stroke', opts.stroke)
      .attr('stroke-width', opts.strokeWidth ?? 1)
      .attr('vector-effect', 'non-scaling-stroke');
  }
  return circle;
}

export function appendLabel(
  group: Selection,
  x: number,
  y: number,
  text: string | number,
  font: number = LABEL_FONT,
) {
  group
    .append('text')
    .attr('x', x)
    .attr('y', y)
    .attr('fill', 'white')
    .attr('original-font-size', font)
    .attr('font-size', `${font}px`)
    .attr('text-anchor', 'middle')
    .attr('dy', '-1')
    .text(text);
}

export function appendSelectionHandle(
  group: Selection,
  centroidPosition: { x: number; y: number },
  color: string,
  moved: boolean,
) {
  appendCircle(group, centroidPosition.x, centroidPosition.y, CENTROID_RADIUS, {
    fill: color,
    fillOpacity: moved ? 0.5 : 0.3,
  });
}

export interface MeasurementBadgeOpts {
  fill?: string;
  fillOpacity?: number;
  stroke?: string;
  strokeWidth?: number;
  fontSize?: number;
  textColor?: string;
  paddingX?: number;
  paddingY?: number;
  borderRadius?: number;
}

export function appendMeasurementBadge(
  group: Selection,
  cx: number,
  cy: number,
  text: string,
  opts: MeasurementBadgeOpts = {},
) {
  const fontSize = opts.fontSize ?? 9.5;
  const paddingX = opts.paddingX ?? 4;
  const paddingY = opts.paddingY ?? 1.2;
  const rx = opts.borderRadius ?? 2.5;
  const approxTextWidth = text.length * (fontSize * 0.58);
  const badgeWidth = Math.max(22, Math.ceil(approxTextWidth + paddingX * 2));
  const badgeHeight = Math.ceil(fontSize + paddingY * 2);

  const badgeGroup = group
    .append('g')
    .attr('class', 'measurement-badge')
    .attr('data-cx', cx)
    .attr('data-cy', cy)
    .attr('transform', `translate(${cx}, ${cy})`)
    .attr('pointer-events', 'auto')
    .style('pointer-events', 'auto')
    .style('cursor', 'pointer')
    .style('filter', 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.8))');

  badgeGroup
    .append('rect')
    .attr('x', -badgeWidth / 2)
    .attr('y', -badgeHeight / 2)
    .attr('width', badgeWidth)
    .attr('height', badgeHeight)
    .attr('rx', rx)
    .attr('ry', rx)
    .attr('fill', opts.fill ?? '#0f0f0f')
    .attr('fill-opacity', opts.fillOpacity ?? 0.94)
    .attr('stroke', opts.stroke ?? 'rgba(255, 255, 255, 0.4)')
    .attr('stroke-width', opts.strokeWidth ?? 0.75)
    .attr('vector-effect', 'non-scaling-stroke')
    .attr('pointer-events', 'auto')
    .style('pointer-events', 'auto')
    .style('cursor', 'pointer');

  badgeGroup
    .append('text')
    .attr('x', 0)
    .attr('y', 0)
    .attr('fill', opts.textColor ?? '#e5e5e5')
    .attr('font-size', `${fontSize}px`)
    .attr('font-weight', '600')
    .attr('font-family', 'Inter, system-ui, -apple-system, sans-serif')
    .attr('text-anchor', 'middle')
    .attr('dominant-baseline', 'central')
    .attr('pointer-events', 'none')
    .style('user-select', 'none')
    .text(text);

  return badgeGroup;
}

export interface GroupTransformCtx {
  overlayTransform: string;
  scaleFactor: number;
  imageToScreenMatrix: { a: number; b: number };
}

// Applies the image->screen affine to the contour group and keeps radii/fonts
// at constant on-screen size (`non-scaling-stroke` handles strokes). Text is
// also counter-rotated so annotations stay upright on rotated images.
export function applyGroupTransform(group: Selection, ctx: GroupTransformCtx) {
  if (!group || group.empty?.()) return;

  group.attr('transform', ctx.overlayTransform);

  // Only counter-scale handles that opted in via `original-r`; raw circles
  // (e.g. the nudge tool) are meant to scale with the image and are left alone.
  const sf = ctx.scaleFactor || 1;
  group
    .selectAll('circle[original-r]')
    .attr('r', function applyRadius(this: d3.BaseType) {
      const node = this as Element;
      const r = Number.parseFloat(node.getAttribute('original-r') ?? '');
      return Number.isFinite(r) ? r / sf : node.getAttribute('r');
    });

  const { a, b } = ctx.imageToScreenMatrix;
  const angleDeg = (Math.atan2(b, a) * 180) / Math.PI;

  // Counter-scale and counter-rotate measurement badges as atomic units
  group.selectAll('g.measurement-badge').each(function applyBadge(this: d3.BaseType) {
    const node = this as Element;
    const cx = Number.parseFloat(node.getAttribute('data-cx') ?? '0');
    const cy = Number.parseFloat(node.getAttribute('data-cy') ?? '0');
    d3.select(node).attr(
      'transform',
      `translate(${cx}, ${cy}) rotate(${-angleDeg}) scale(${1 / sf})`,
    );
  });

  // Counter-scale standalone annotation text (excluding badge text)
  group.selectAll('text[original-font-size]').each(function applyText(this: d3.BaseType) {
    const node = this as Element;
    const el = d3.select(node);
    const f
      = Number.parseFloat(node.getAttribute('original-font-size') ?? '')
        || LABEL_FONT;
    el.attr('font-size', `${f / sf}px`);
    const x = Number.parseFloat(node.getAttribute('x') ?? '') || 0;
    const y = Number.parseFloat(node.getAttribute('y') ?? '') || 0;
    el.attr('transform', `rotate(${-angleDeg}, ${x}, ${y})`);
  });
}
